import { useEffect, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { TextFieldProps, CircularProgress } from '@material-ui/core'
import { usePrevious } from 'shared/hooks'
import { TextField, InputLabel, LOADER_SIZE } from './FormikTextfield.style'

interface BaseFormikTextfieldProps {
  name: string
  label?: string | JSX.Element | (string | JSX.Element)[]
  placeholder?: string
  isLoading?: boolean
  validateWithoutBlur?: boolean
  labelSize?: number
  wrapError?: boolean
  labelComponent?: typeof InputLabel
  transformValue?: (value: string) => string

  startAdornment?: React.ReactNode
}

type FormikTextfieldProps = BaseFormikTextfieldProps & TextFieldProps

const FormikTextfield = ({
  label,
  isLoading,
  validateWithoutBlur,
  labelSize,
  wrapError = false,
  labelComponent,
  transformValue,
  startAdornment,
  ...props
}: FormikTextfieldProps) => {
  const [textInserted, setTextInserted] = useState(false)
  const context = useFormikContext()
  const prevLoading = usePrevious(isLoading)
  const [field, meta] = useField(props.name)
  const prevValue = usePrevious(meta.value)
  const isErrorWithoutBlur = !props.disabled && !!meta.error
  const isError =
    props.error ||
    (validateWithoutBlur && textInserted
      ? isErrorWithoutBlur
      : isErrorWithoutBlur && meta.touched)

  useEffect(() => {
    if (prevLoading === true && isLoading === false) {
      context.validateField(props.name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  useEffect(() => {
    if (typeof prevValue === 'string' && prevValue !== meta.value) {
      setTextInserted(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.value])

  const LabelComponent = labelComponent || InputLabel

  return (
    <>
      <LabelComponent
        labelSize={labelSize}
        error={isError}
        disabled={props.disabled}
      >
        {label}
      </LabelComponent>
      <TextField
        {...props}
        {...field}
        {...(isLoading
          ? {
              InputProps: {
                endAdornment: <CircularProgress size={LOADER_SIZE} />,
              },
            }
          : undefined)}
        onChange={async e => {
          await field.onChange(e)
          props.onChange && props.onChange(e)
        }}
        onBlur={async e => {
          field.onBlur(e)
          props.onBlur && props.onBlur(e)
        }}
        value={transformValue ? transformValue(field.value) : field.value}
        error={isError}
        wrapHelperText={wrapError}
        helperText={isError ? props.helperText || meta.error : ''}
      />
    </>
  )
}

export default FormikTextfield
